<template>
  <div
    ref="message"
    class="hint-message"
    :class="{
      'hint-message--open': isOpen,
      'hint-message--right': isRight,
      'hint-message--bottom': isBottom,
    }"
    :style="messagePosition"
    v-on="$listeners"
    @click.stop
  >
    <p v-dompurify-html="message" class="hint-message__text" />
  </div>
</template>

<script>
import lang from '@/mixins/lang';

const MINIMUM_DISTANCE_WITH_PAGE_BOUNDS = 100;

export default {
  mixins: [lang],
  props: {
    message: {
      type: String,
      required: true,
    },
    isOpen: {
      type: Boolean,
      required: true,
    },
    wrapperPosition: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      width: 0,
      height: 0,
    };
  },
  computed: {
    isLTR() {
      return this.normalizedLang === 'en';
    },
    isRight() {
      const minDistanceWithBorder =
        this.width + MINIMUM_DISTANCE_WITH_PAGE_BOUNDS;

      if (this.isLTR) {
        // Prioritize opening hints in the right side
        return (
          this.wrapperPosition.left < window.innerWidth - minDistanceWithBorder
        );
      }

      // Prioritize opening hints in the left side
      return this.wrapperPosition.left < minDistanceWithBorder;
    },
    isBottom() {
      return (
        this.wrapperPosition.top <
        this.height + MINIMUM_DISTANCE_WITH_PAGE_BOUNDS
      );
    },
    messagePosition() {
      const position = {};

      if (this.isBottom) {
        position.top = '25px';
      } else {
        position.bottom = '25px';
      }

      if (this.isRight) {
        position.left = '-19px';
      } else {
        position.right = '-18px';
      }

      return position;
    },
  },
  watch: {
    message() {
      this.calculateMessageSize();
    },
  },
  mounted() {
    this.calculateMessageSize();
  },
  methods: {
    calculateMessageSize() {
      const { width, height } = this.$refs.message.getBoundingClientRect();

      this.width = width;
      this.height = height;
    },
  },
};
</script>

<style lang="scss">
$component-name: 'hint-message';
$arrow-distance-from-edge: 20px;

.#{$component-name} {
  background: #fff;
  padding: $global-padding * 0.8 $global-padding;
  border-radius: 5px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
  width: rem-calc(400);
  transition-duration: 0.3s;
  text-align: left;
  direction: ltr;
  z-index: 9999;
  cursor: default;
  opacity: 0;
  transform: translateY(20px);
  pointer-events: none;

  &--bottom {
    transform: translateY(-20px);
  }

  &--open {
    transform: none;
    pointer-events: auto;
    opacity: 1;
  }

  @include rtl() {
    text-align: right;
    direction: rtl;
  }

  &::after {
    content: '';
    position: absolute;
    background: #fff;
    width: 10px;
    height: 10px;
    transform: rotate(45deg);
    right: $arrow-distance-from-edge + 3px;
    bottom: -4px;
  }

  &--right::after {
    right: auto;
    left: $arrow-distance-from-edge + 3px;
  }

  &--bottom::after {
    bottom: auto;
    top: -4px;
  }

  &__text {
    margin: 0;
    padding: 0;
    font-size: 0.8rem;
    font-weight: normal;
    color: #000;
    white-space: normal;
  }
}
</style>
